/**
 * Closing the account, against `POST /api/v1/account/deletion/`.
 *
 * Its own module rather than a function in `settingsService`: that file is the
 * General tab's form — a `GET` and a `PATCH` of one profile — and this is the
 * call that removes the workspace, every client on it, their plans and the
 * whole revenue history. Nothing here is shared with it, and the two should not
 * be reached for by the same import.
 *
 * One request, one answer, and no record comes back: there is none left. What
 * the endpoint returns is a sentence to show, which is what this hands on.
 *
 * The session outlives the account. Deleting is the backend's job and ending
 * the Clerk session is the browser's, so the caller signs out afterwards — see
 * `AccountsTab`.
 */

import { ApiError, api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type { AccountDeletionResponse } from "../types/settings.types";

/**
 * The phrase the backend checks the request against.
 *
 * A constant of the protocol, not the word the dialog asks to be typed — those
 * are two different safeguards against two different accidents. The dialog's
 * `DELETE` guards against a person pressing the button without meaning it; this
 * guards against a `POST` to this path arriving without a deletion behind it,
 * which is why it is written here rather than passed in from a text box: a
 * confirmation the caller can vary is not one the server can rely on.
 */
const CONFIRMATION = "DELETE MY ACCOUNT";

/** What is shown when the server ended the account but said nothing about it. */
const DELETED_MESSAGE =
  "Your account and all associated data have been permanently deleted.";

/**
 * Deletes the caller's account, and answers with the backend's own account of
 * it — the sentence to show while the session is being ended.
 *
 * Every failure throws. A caller must not treat a refused deletion as a done
 * one: an account that is still there being reported as gone is worse than an
 * error, because the user stops looking for it.
 *
 * No `signal`. A deletion that has reached the server cannot be called back,
 * and aborting the request would only lose the answer to something that
 * happened anyway.
 */
export async function deleteAccount(getToken: GetToken): Promise<string> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Reaching this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot delete your account.");
  }

  const response = await api.post<AccountDeletionResponse>(
    API_ENDPOINTS.account.deletion,
    { confirmation: CONFIRMATION },
    { token }
  );

  return response?.message?.trim() || DELETED_MESSAGE;
}

/**
 * What went wrong, as a line to put under "Could not delete your account".
 *
 * A server that refused said why — the confirmation phrase, an outstanding
 * subscription — and that message is more use than anything written here.
 * Anything else never reached it, and says so.
 */
export const deletionFailureMessage = (error: unknown): string =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";
